/**
 * Calendar days as `yyyy-MM-dd`, the form of Date Only columns and delivery dates. `new Date` reads
 * a bare day as UTC midnight, the day before west of Greenwich, so a day becomes a moment at local
 * midnight here instead.
 */
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDay(value: unknown): value is string {
  return typeof value === 'string' && ISO_DAY.test(value);
}

/** `2026-09-29` → `2026-09-29T00:00:00`, local midnight; a value with a time is returned as it is. */
export function localMidnight(value: string): string {
  return ISO_DAY.test(value) ? `${value}T00:00:00` : value;
}

/** The day `moment` falls on in the device's calendar: the UTC day is yesterday in the Americas evening. */
export function localIsoDay(moment: Date = new Date()): string {
  return `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`;
}

/**
 * A User Local column read back, e.g. `2026-11-02T18:30:00Z` for a day the app wrote as local
 * midnight in India, as the day it is here; a bare day stays as it is.
 */
export function localDayOf(value: string | null | undefined): string | null {
  if (!value) return null;
  if (ISO_DAY.test(value)) return value;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : localIsoDay(new Date(time));
}

/**
 * `2026-09-29` → `2026-09-29T00:00:00+05:30`: local midnight with the device's offset, for a
 * User Local column. The server refuses a bare date string there.
 */
export function toDateTimeOffset(
  date: string | null | undefined,
): string | null {
  const match = ISO_DAY.exec(date ?? '');
  if (!match) return date ?? null;
  const offset = -new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  ).getTimezoneOffset();
  const sign = offset < 0 ? '-' : '+';
  const minutes = Math.abs(offset);
  return `${date}T00:00:00${sign}${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
